import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseComponent } from '@lib/components';
import { ApiResponseError, IAccountFundsResponse, IFormGroupSchema, ILoadFundsForm } from '@lib/interfaces';
import { ValueFieldModule } from '@lib/modules';
import { AccountsService } from '@lib/services';
import { finalize } from 'rxjs';
import { AngularMaterialModule } from 'src/app/material.module';

export interface IPayBillForm extends ILoadFundsForm {
    used: number;
    available: number;
    total: 10000;
}

@Component({
    standalone: true,
    selector: 'app-pay-bill',
    templateUrl: './pay-bill.component.html',
    styleUrls: ['./pay-bill.component.scss'],
    imports: [AngularMaterialModule, ValueFieldModule],
})
export class PayBillComponent extends BaseComponent implements OnInit {
    protected readonly currencies = ['SAR'];
    protected isLoading = false;
    protected readonly total = 10000;

    protected payBillForm: FormGroup<IFormGroupSchema<Partial<IPayBillForm>>>;

    constructor(
        private readonly formBuilder: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: { accountId: any; balance: number },
        private snackbar: MatSnackBar,
        private payBillRef: MatDialogRef<PayBillComponent>,
        private accountsService: AccountsService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.payBillForm = this.initializeForm();
    }

    protected initializeForm(): FormGroup<IFormGroupSchema<Partial<IPayBillForm>>> { 
        return new FormGroup<IFormGroupSchema<Partial<IPayBillForm>>>(
            {
                amount: this.formBuilder.nonNullable.control(this.total - this.data.balance, [Validators.required, Validators.min(1)]),
                note: this.formBuilder.nonNullable.control(''),
                currency: this.formBuilder.nonNullable.control('SAR', [Validators.required]),
                used: this.formBuilder.nonNullable.control(this.total - this.data.balance),
                available: this.formBuilder.nonNullable.control(this.data.balance),
                total: this.formBuilder.nonNullable.control(this.total),
                dry_run: this.formBuilder.nonNullable.control(false),
            },
            { validators: this.amountLimitValidator },
        );
    }

    protected closeModal(paidBill = false) {
        this.payBillRef.close(paidBill);
    }

    protected loadFunds() {
        if (this.payBillForm.valid) {
            this.isLoading = true;
            const formValue = this.payBillForm.value;
            const payload: ILoadFundsForm = {
                currency: formValue.currency!,
                amount: formValue.amount!,
                note: formValue.note? formValue.note : 'Added by user',
                dry_run: formValue.dry_run!,
            };
            this.accountsService
                .loadFunds(payload, this.data.accountId)
                .pipe(
                    finalize(() => {
                        this.isLoading = false;
                    }),
                )
                .subscribe({
                    next: (accountFunds: IAccountFundsResponse): void => {
                        if (accountFunds?.result.status === 'DECLINED') {
                            this.createApiErrorSnackBar(this.snackbar, accountFunds?.result.status_description || accountFunds?.result.status_reason);
                        }
                        this.createApiSuccessSnackBar(this.snackbar, 'Payment Received. Thank you!');
                        this.closeModal(true);
                    },
                    error: (error: ApiResponseError): void => {
                        this.createApiErrorSnackBar(this.snackbar, error);
                    },
                });
        }
    }

    protected amountLimitValidator(form: AbstractControl): ValidationErrors | null {
        const used = form.get('used')?.value;
        const amount = form.get('amount')?.value;

        if (used === 0) {
            return { zeroLimit: true };
        }

        if (amount === null || amount === undefined) return null;

        if (amount < 1) {
            return { minAmount: true };
        }

        if (amount > used) {
            return { maxLimit: true };
        }

        return null;
    }
}
